/**
 * Events Context — wires the runtime event bus into the React tree.
 *
 * One bus instance per VibesitesProvider. The instance lives in a useMemo so
 * its identity is stable across re-renders and subscribers don't lose state.
 * The DOM-capture listener is attached strictly inside a useEffect so it
 * never runs during renderToString (SSR-safe by construction).
 *
 * Components consume the bus via `useVibesiteEvents()`.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import {
  createEventBus,
  mountDomCapture,
  type VibesiteEventBus,
  type VibesiteEventListener,
  type Unsubscribe,
  type VibesiteEventEnvelope,
  type VibesiteEventName,
  type VibesiteEventPayloadMap,
} from '../events';
import { useProperty, usePage } from './context';
import { EventsDebugOverlay } from './events-debug-overlay';

// ============================================================================
// Context
// ============================================================================

const EventsContext = createContext<VibesiteEventBus | null>(null);

// ============================================================================
// Provider
// ============================================================================

export interface EventsProviderProps {
  children: ReactNode;
}

/**
 * Mounts the bus + DOM capture for the current VibesitesProvider subtree.
 * Read property/page from the parent runtime context so envelope fields
 * (page, path, propertyId, companyId) are attached to every emission.
 *
 * Must be rendered inside <VibesitesContext.Provider> so useProperty/usePage
 * resolve.
 */
export function EventsProvider({ children }: EventsProviderProps) {
  const property = useProperty();
  const page = usePage();

  // Stable bus instance for this provider. Pure JS — safe in SSR.
  const bus = useMemo(() => createEventBus(), []);

  // Hold the latest envelope context in a ref so the DOM-capture listener
  // (mounted once) always reads up-to-date page / property data without
  // re-attaching on every navigation.
  const envelopeRef = useRef<VibesiteEventEnvelope>({});
  envelopeRef.current = {
    page: page?.alias,
    path: page?.path,
    propertyId: property?.multihub_property_id ?? undefined,
    companyId: property?.multihub_company_id ?? undefined,
  };

  // Mount DOM capture exactly once per provider instance. The useRef guard
  // is belt-and-suspenders against a future refactor that would make the
  // effect dependency-driven; today the empty dep array already handles it.
  const mountedRef = useRef(false);
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    const cleanup = mountDomCapture({
      bus,
      getEnvelope: () => envelopeRef.current,
    });

    // TODO: Expose the bus on `window.vibesites.events` when we need to
    // integrate non-React event sources. Concrete future cases:
    //   1. Third-party chatbot vendors (Elise, RentCafe, Zuma, Rentgrata)
    //      whose submit events arrive via vendor postMessage / global
    //      callbacks attached outside the React tree.
    //   2. The Multihub `leadcapture.js` script, which is the authoritative
    //      point where a lead beacon is persisted and lead IDs are issued —
    //      the React lead-form block only knows that submitAsync resolved.
    //   3. AI-generated or per-property custom JS snippets that need to
    //      emit semantic events without authoring a full block.
    //
    // Implementation when added:
    //   const w = window as unknown as { vibesites?: { events?: unknown } };
    //   w.vibesites = w.vibesites || {};
    //   w.vibesites.events = { emit: bus.emit, subscribe: bus.subscribe };
    //   // Validate event names against the catalog at call time
    //   // (warn-and-drop on miss — bus.emit() already does this).

    return () => {
      cleanup();
      mountedRef.current = false;
    };
  }, [bus]);

  return (
    <EventsContext.Provider value={bus}>
      {children}
      {/* Debug overlay — renders only when ?vibe_events=1 is in the URL or the
          localStorage flag is set. No-op for normal renders / SSR. */}
      <EventsDebugOverlay />
    </EventsContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

export interface UseVibesiteEventsResult {
  /** Emit a canonical event to all subscribers. */
  emit: <N extends VibesiteEventName>(
    name: N,
    payload?: VibesiteEventPayloadMap[N],
  ) => void;
  /**
   * Subscribe to a single event or '*' for everything. Returns an
   * unsubscribe function — typically returned from a useEffect.
   */
  subscribe: <N extends VibesiteEventName | '*'>(
    name: N,
    listener: VibesiteEventListener<N>,
  ) => Unsubscribe;
}

/**
 * Hook for accessing the runtime event bus. Returns a stable object — safe
 * to use as a useEffect dependency.
 *
 * Throws if used outside an `EventsProvider` (which is mounted by every
 * `VibesitesProvider`).
 */
export function useVibesiteEvents(): UseVibesiteEventsResult {
  const bus = useContext(EventsContext);
  if (!bus) {
    throw new Error(
      'useVibesiteEvents must be used within a VibesitesProvider (which mounts EventsProvider internally).',
    );
  }
  return bus;
}
